import { ChangeDetectionStrategy, Component, DestroyRef, OnInit } from '@angular/core';
import { catchError, EMPTY, map, Observable, combineLatest, of, switchMap, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

import { PortalLeadService } from '@app/services/portal-lead/portal-lead.service';
import { PortalLeadNode } from '@app/services/api/api.types';
import { StateService } from '@app/services/state/state.service';
import { PopupsService } from '@voyagu/portal-design-system';
import { Tracking } from '@app/services/tracking/tracking.service';
import { CommonDataService } from '@app/services/common-data/common-data.service';
import { PortalRoutes } from '@app/app-routing.module';
import { AuthService } from '@app/services/auth/auth.service';
import { ApiService } from '@app/services/api/api.service';
import { PasscodeModalService } from '@app/services/passcode-modal.service';
import { ErrorService } from '@app/services/error.service';

@Component({
  selector: 'lead-wrapper-page',
  templateUrl: './lead-wrapper-page.component.html',
  styleUrls: ['./lead-wrapper-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeadWrapperPageComponent implements OnInit {
  portalLead$: Observable<PortalLeadNode | null>;

  showLoginButton: boolean;

  isBar: boolean = false;

  constructor(
    private portalLeadService: PortalLeadService,
    private activatedRoute: ActivatedRoute,
    private popupsService: PopupsService,
    private stateService: StateService,
    private router: Router,
    private commonDataService: CommonDataService,
    private destroyRef: DestroyRef,
    private authService: AuthService,
    private apiService: ApiService,
    private passcodeModalService: PasscodeModalService,
    private errorService: ErrorService,
  ) {
    const portalLink = this.activatedRoute.snapshot.paramMap.get('lead-id') ?? '';
    this.stateService.patchState({ portalLink });
  }

  ngOnInit() {
    const handleShowLoginButtonVisibility = this.handleShowLoginButtonVisibility.bind(this);

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      handleShowLoginButtonVisibility();
      const currentRoute = this.getCurrentRoute(this.activatedRoute);
      const path = currentRoute.snapshot.data['path'] as PortalRoutes;
      this.isBar = Boolean(path !== PortalRoutes.Complete && path !== PortalRoutes.Lead);
    });

    this.passcodeModalService.unlocked$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((portalLink) => {
        if (portalLink === this.getPortalLink()) {
          this.getPortalLead();
        }
      });

    this.initPortalLeadStream();
    this.initPortalLeadAccess();
  }

  getCurrentRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  getPortalLead() {
    this.portalLeadService.setPortalLead(null);

    const portalLink = this.getPortalLink();

    Tracking.patch({ leadPortalLink: portalLink });

    this.portalLeadService.getPortalLead({ portalLink });
  }

  initPortalLeadStream() {
    const portalLink = this.getPortalLink();

    this.portalLead$ = combineLatest([
      this.portalLeadService.portalLead$,
      this.commonDataService.getData(),
    ]).pipe(
      map(([portalLead, commonData]) => {
        this.stateService.patchState({ portalLink });
        if (portalLead) {
          if (portalLead.flightRequest?.isLost) {
            window.location.href = commonData.url;
          }

          this.handleShowLoginButtonVisibility();
        }

        return portalLead;
      }),
      catchError((error: Error) => {
        console.log(error.message || error);
        return EMPTY;
      }),
    );
  }

  initPortalLeadAccess() {
    this.portalLeadService.setPortalLead(null);

    const portalLink = this.getPortalLink();

    Tracking.patch({ leadPortalLink: portalLink });

    this.authService
      .hasSession()
      .pipe(
        take(1),
        switchMap((hasSession) => {
          if (hasSession) {
            Tracking.patch({ authorized: true });
            return of({ isLocked: false });
          }

          return this.apiService.portalQuoteAccessState({ portalLink });
        }),
        catchError((error: Error) => {
          console.log(error.message || error);
          this.errorService.showCommonError();
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ isLocked }) => {
        if (isLocked) {
          this.passcodeModalService.showModal(portalLink);
          return;
        }

        this.getPortalLead();
      });
  }

  getPortalLink() {
    return this.activatedRoute.snapshot.paramMap.get('lead-id') || '';
  }

  handleShowLoginButtonVisibility() {
    const routeLevel = this.router.url.split('/').filter((v) => !!v)?.length; // todo probably bug

    this.showLoginButton = routeLevel === 1;
  }

  hideModal() {
    this.popupsService.hideLast();
  }
}
